import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchProducts } from "../../redux/slices/productSlice";
import { createOpeningStockThunk } from "../../redux/slices/purchaseSlice";
import { getApiErrorMessage } from "../../utils/apiError";

const today = () => new Date().toISOString().split("T")[0];

const EMPTY = { productID: "", quantity: "", purchaseRate: "", asOnDate: today() };

const OpeningStockModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const products = useSelector((s) => s.products.list);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const productOptions = products
    .filter((p) => p.status === "Active")
    .map((p) => ({ value: p.productID || p.id, label: p.itemName, rate: p.purchaseRate }));

  useEffect(() => { dispatch(fetchProducts()); }, [dispatch]);
  useEffect(() => { if (show) setForm({ ...EMPTY, asOnDate: today() }); }, [show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.productID || !form.quantity || !form.asOnDate) {
      Swal.fire("Required", "Product, Quantity and Date are required.", "warning"); return;
    }
    setSaving(true);
    try {
      await dispatch(createOpeningStockThunk(form)).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Opening stock recorded", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to record opening stock."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title"><h4>Record Opening Stock</h4></div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Product <span className="text-danger">*</span></label>
                    <Select classNamePrefix="react-select" options={productOptions}
                      value={productOptions.find((o) => o.value === form.productID) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, productID: opt?.value || "", purchaseRate: opt?.rate || "" }))}
                      placeholder="Select product"
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Quantity <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" min={0} value={form.quantity} onChange={set("quantity")} placeholder="0" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Purchase Rate (₹)</label>
                    <input type="number" className="form-control" min={0} step="0.01" value={form.purchaseRate} onChange={set("purchaseRate")} placeholder="0.00" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">As On Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={form.asOnDate} onChange={set("asOnDate")} />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : "Record Stock"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default OpeningStockModal;
